import { clamp, isZero } from '@studio/models/ptz';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import { CommandDeviceMove } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { activeMovementDevices, activeMovementTools, lastIntent, timeout } from './constants.js';
import { stop } from './stop.js';
import { isTrialCameraLocked } from '#~/api/billing/camera-access.js';
import type { Move } from './types.js';
import { locateProxy } from '#~/agent/index.js';
// Gating (spec §6): movement/PTZ over a transitory placeholder is forbidden
// until auth. Per `reference-rules-in-comments` (ai.knowledge/global).
import { isTransitoryId } from '#~/device/gating.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { track } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';

/**
 * Move the specified device.
 *
 * @param deviceId - The ID of the device to be moved.
 * @param intent_ - Movement intent.
 */
export const move: Move = (deviceId, intent_, options) => {
  // Gating §6 — movement/PTZ capability (the physical IP joystick + the
  // on-canvas <Interaction> both route here). A placeholder has no proxy, so
  // the `!proxy` branch below already no-ops; this explicit bail documents
  // the gate at the chokepoint and short-circuits before `clamp`/`stop`.
  // The sibling movement commands (move-to, zoom-to, quick-move, home, stop,
  // quick-shots) ALL bail on `!proxy` already (verified `@6370b897b`), so a
  // placeholder — which never resolves a proxy — is a no-op across the set.
  if (isTransitoryId(deviceId)) {
    return;
  }

  const timestamp = Date.now();
  const intent = clamp(intent_);
  const toolUsed = options?.toolUsed ?? 'unknown';

  if (isZero(intent)) {
    stop(deviceId, undefined, { toolUsed });

    return;
  }

  const proxy = locateProxy(deviceId);

  if (!proxy) {
    return;
  }
  // Direct movement bypasses ProxyImpl.mutate; stop remains available above.
  if (isTrialCameraLocked(proxy.roomId, proxy.id, deviceId)) return;

  // Do we need to stop certain movement vectors first?
  const last = lastIntent.get(deviceId);

  if (last) {
    const wasPan = !isZero(last.pan);
    const wasTilt = !isZero(last.tilt);
    const wasZoom = !isZero(last.zoom);

    const willPan = !isZero(intent.pan);
    const willTilt = !isZero(intent.tilt);
    const willZoom = !isZero(intent.zoom);

    if ((wasPan && !willPan) || (wasTilt && !willTilt) || (wasZoom && !willZoom)) {
      stop(
        deviceId,
        {
          pan: wasPan && !willPan,
          tilt: wasTilt && !willTilt,
          zoom: wasZoom && !willZoom
        },
        { toolUsed }
      );
    }
  }

  if (timestamp - (last?.timestamp ?? 0) < timeout) {
    const panSame = last?.pan === intent.pan;
    const tiltSame = last?.tilt === intent.tilt;
    const zoomSame = last?.zoom === intent.zoom;

    if (panSame && tiltSame && zoomSame) {
      return;
    }
  }

  if (!activeMovementDevices.has(deviceId)) {
    activeMovementDevices.add(deviceId);
    activeMovementTools.set(deviceId, toolUsed);
    const deviceAnalyticsContext = resolveDeviceAnalyticsContext(deviceId) ?? {
      device_id: deviceId
    };

    void track({
      category: EVENT_CATEGORIES.DEVICE,
      name: EVENT_NAMES.DEVICE_MOVE_START,
      properties: {
        ...deviceAnalyticsContext,
        tool_used: toolUsed
      }
    } as AnalyticsEvent);
  }

  // Save this intent so that we can consult it on the next move command.
  lastIntent.set(deviceId, {
    ...intent,
    timestamp
  });

  // Compute this mutation.
  let { pan, tilt, zoom } = intent;

  const mutation: DeepPartial<CommandDeviceMove> = {
    id: deviceId,
    panTilt:
      pan || tilt
        ? {
            pan,
            tilt
          }
        : undefined,
    zoom: zoom ? { zoom } : undefined
  };

  const mutB = CommandDeviceMove.encode(CommandDeviceMove.fromJSON(mutation)).finish();

  proxy.connection.publish(`direct.authenticated.${proxy.id}.move`, mutB);
};
